import React, { useState, useEffect } from 'react';
import { CircleDot, Building2, Settings, Eye, BarChart3, Loader2, ClipboardList, Rocket, AlertTriangle, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { sportsApi } from '../../services/sportsApi';
import { 
  openLigaDbApi, 
  OpenLigaImportConfig, 
  OpenLigaImportResult,
  OpenLigaMatch,
  SUPPORTED_LEAGUES 
} from '../../services/openLigaDbApi';
import { Club, League } from '../../types/sports';

const OpenLigaDbImport: React.FC = () => {
  const { user } = useAuth();
  const [userClub, setUserClub] = useState<Club | null>(null);
  const [userLeague, setUserLeague] = useState<League | null>(null);
  
  // Import configuration
  const [selectedLeague, setSelectedLeague] = useState('bl1');
  const [selectedSeason, setSelectedSeason] = useState(new Date().getFullYear());
  const [selectedMatchday, setSelectedMatchday] = useState<number | undefined>();
  const [updateExisting, setUpdateExisting] = useState(true);
  const [createMissingClubs, setCreateMissingClubs] = useState(true);
  const [createMissingMatchdays, setCreateMissingMatchdays] = useState(true);
  
  // State
  const [previewData, setPreviewData] = useState<OpenLigaMatch[]>([]);
  const [importResult, setImportResult] = useState<OpenLigaImportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'config' | 'preview' | 'result'>('config');

  // Load user's preferred club
  useEffect(() => {
    const loadUserClub = async () => {
      if (user?.preferredClubId) {
        try {
          const club = await sportsApi.getClubById(user.preferredClubId);
          setUserClub(club);
          if (club.league) {
            setUserLeague(club.league);
            // Auto-select matching league
            const matchingLeague = SUPPORTED_LEAGUES.find(l => 
              l.name.toLowerCase().includes(club.league?.name?.toLowerCase() || '')
            );
            if (matchingLeague) {
              setSelectedLeague(matchingLeague.shortcut);
            }
          }
        } catch (err) {
          console.error('Error loading user club:', err);
        }
      }
    };
    loadUserClub();
  }, [user?.preferredClubId]);

  // Generate season options (last 5 years + next year)
  const currentYear = new Date().getFullYear();
  const seasonOptions = Array.from({ length: 6 }, (_, i) => currentYear - 4 + i);

  // Load preview matches
  const handleLoadPreview = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const matches = await openLigaDbApi.getMatches(
        selectedLeague, 
        selectedSeason, 
        selectedMatchday
      );
      setPreviewData(matches);
      setActiveTab('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setIsLoading(false);
    }
  };

  // Perform import
  const handleImport = async () => {
    setIsImporting(true);
    setError(null);
    try {
      const config: OpenLigaImportConfig = {
        leagueShortcut: selectedLeague,
        season: selectedSeason,
        matchdayNumber: selectedMatchday,
        updateExisting,
        createMissingClubs,
        createMissingMatchdays,
        filterByClubName: userClub?.name, // Only import matches for user's club
      };
      const result = await openLigaDbApi.importMatches(config);
      setImportResult(result);
      setActiveTab('result');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  // Filter matches to show only user's club matches
  // Uses flexible matching: club name, short name, or common abbreviations
  const getUserClubMatches = () => {
    if (!userClub) return previewData;

    // Build list of search terms from club name and short name
    const searchTerms: string[] = [];

    // Add full name and short name
    if (userClub.name) searchTerms.push(userClub.name.toLowerCase());
    if (userClub.shortName) searchTerms.push(userClub.shortName.toLowerCase());

    // Extract key words from club name (e.g., "Hamburger" from "Hamburger Sportverein")
    const nameWords = userClub.name.split(' ').filter(w => w.length > 3);
    nameWords.forEach(word => searchTerms.push(word.toLowerCase()));

    return previewData.filter(m => {
      const team1Name = m.team1?.teamName?.toLowerCase() || '';
      const team2Name = m.team2?.teamName?.toLowerCase() || '';
      const team1Short = m.team1?.shortName?.toLowerCase() || '';
      const team2Short = m.team2?.shortName?.toLowerCase() || '';

      return searchTerms.some(term =>
        team1Name.includes(term) || team2Name.includes(term) ||
        team1Short.includes(term) || team2Short.includes(term)
      );
    });
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={titleStyle}><CircleDot size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> OpenLigaDB Import</h2>
        {userClub && (
          <div style={clubInfoStyle}>
            <span style={clubBadgeStyle}><Building2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {userClub.name}</span>
            {userLeague && <span style={leagueBadgeStyle}>{userLeague.name}</span>}
          </div>
        )}
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      {/* Tab Navigation */}
      <div style={tabContainerStyle}>
        <button 
          style={activeTab === 'config' ? activeTabStyle : tabStyle}
          onClick={() => setActiveTab('config')}
        >
          <Settings size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Konfiguration
        </button>
        <button 
          style={activeTab === 'preview' ? activeTabStyle : tabStyle}
          onClick={() => setActiveTab('preview')}
          disabled={previewData.length === 0}
        >
          <Eye size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Vorschau ({previewData.length})
        </button>
        <button 
          style={activeTab === 'result' ? activeTabStyle : tabStyle}
          onClick={() => setActiveTab('result')}
          disabled={!importResult}
        >
          <BarChart3 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Ergebnis
        </button>
      </div>

      {/* Config Tab */}
      {activeTab === 'config' && (
        <div style={configContainerStyle}>
          <div style={formRowStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Liga</label>
              <select
                style={selectStyle}
                value={selectedLeague}
                onChange={(e) => setSelectedLeague(e.target.value)}
              >
                {SUPPORTED_LEAGUES.map(league => (
                  <option key={league.shortcut} value={league.shortcut}>
                    {league.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Saison</label>
              <select
                style={selectStyle}
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(parseInt(e.target.value))}
              >
                {seasonOptions.map(year => (
                  <option key={year} value={year}>
                    {year}/{year + 1}
                  </option>
                ))}
              </select>
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Spieltag (optional)</label>
              <input
                type="number"
                style={inputStyle}
                placeholder="Alle Spieltage"
                min={1}
                max={34}
                value={selectedMatchday || ''}
                onChange={(e) => setSelectedMatchday(e.target.value ? parseInt(e.target.value) : undefined)}
              />
            </div>
          </div>

          <div style={checkboxContainerStyle}>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
              />
              Bestehende Spiele aktualisieren
            </label>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={createMissingClubs}
                onChange={(e) => setCreateMissingClubs(e.target.checked)}
              />
              Fehlende Vereine anlegen
            </label>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={createMissingMatchdays}
                onChange={(e) => setCreateMissingMatchdays(e.target.checked)}
              />
              Fehlende Spieltage anlegen
            </label>
          </div>

          <div style={buttonContainerStyle}>
            <button
              style={primaryButtonStyle}
              onClick={handleLoadPreview}
              disabled={isLoading}
            >
              {isLoading ? <><Loader2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Laden...</> : <><Eye size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Vorschau laden</>}
            </button>
          </div>
        </div>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <div style={previewContainerStyle}>
          <div style={previewHeaderStyle}>
            <span><ClipboardList size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {previewData.length} Spiele gefunden</span>
            {userClub && (
              <span style={filterBadgeStyle}>
                {getUserClubMatches().length} Spiele von {userClub.shortName || userClub.name}
              </span>
            )}
          </div>
          <div style={matchListStyle}>
            {(userClub ? getUserClubMatches() : previewData.slice(0, 50)).map(match => (
              <div key={match.matchId} style={matchCardStyle}>
                <div style={matchDateStyle}>
                  {new Date(match.matchDateTime).toLocaleDateString('de-DE', {
                    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric'
                  })}
                  {' '}
                  {new Date(match.matchDateTime).toLocaleTimeString('de-DE', {
                    hour: '2-digit', minute: '2-digit'
                  })}
                </div>
                <div style={matchTeamsStyle}>
                  <span style={getTeamStyle(match.team1?.teamName)}>{match.team1?.teamName || 'TBD'}</span>
                  <span style={vsStyle}>vs</span>
                  <span style={getTeamStyle(match.team2?.teamName)}>{match.team2?.teamName || 'TBD'}</span>
                </div>
                <div style={matchInfoStyle}>
                  {match.group?.groupName} • {match.matchIsFinished ? <><CheckCircle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> Beendet</> : <><Clock size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> Geplant</>}
                </div>
              </div>
            ))}
          </div>
          <div style={buttonContainerStyle}>
            <button
              style={secondaryButtonStyle}
              onClick={() => setActiveTab('config')}
            >
              ← Zurück
            </button>
            <button
              style={importButtonStyle}
              onClick={handleImport}
              disabled={isImporting || previewData.length === 0}
            >
              {isImporting ? <><Loader2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Importiere...</> : <><Rocket size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {userClub ? getUserClubMatches().length : previewData.length} Spiele importieren</>}
            </button>
          </div>
        </div>
      )}

      {/* Result Tab */}
      {activeTab === 'result' && importResult && (
        <div style={resultContainerStyle}>
          <div style={importResult.success ? successBoxStyle : errorBoxStyle}>
            <h3>{importResult.success ? <><CheckCircle size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Import erfolgreich!</> : <><XCircle size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Import fehlgeschlagen</>}</h3>
            <p>{importResult.message}</p>
          </div>
          <div style={statsGridStyle}>
            <div style={statCardStyle}>
              <div style={statNumberStyle}>{importResult.matchesImported}</div>
              <div style={statLabelStyle}>Spiele importiert</div>
            </div>
            <div style={statCardStyle}>
              <div style={statNumberStyle}>{importResult.matchesUpdated}</div>
              <div style={statLabelStyle}>Spiele aktualisiert</div>
            </div>
            <div style={statCardStyle}>
              <div style={statNumberStyle}>{importResult.clubsCreated}</div>
              <div style={statLabelStyle}>Vereine angelegt</div>
            </div>
            <div style={statCardStyle}>
              <div style={statNumberStyle}>{importResult.matchdaysCreated}</div>
              <div style={statLabelStyle}>Spieltage angelegt</div>
            </div>
          </div>
          {importResult.warnings.length > 0 && (
            <div style={warningsStyle}>
              <h4><AlertTriangle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Warnungen</h4>
              <ul>{importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          {importResult.errors.length > 0 && (
            <div style={errorsStyle}>
              <h4><XCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Fehler</h4>
              <ul>{importResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
          <div style={buttonContainerStyle}>
            <button style={primaryButtonStyle} onClick={() => { setActiveTab('config'); setImportResult(null); }}>
              <RefreshCw size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Neuer Import
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Helper function to highlight user's club (uses same flexible matching)
  function getTeamStyle(teamName?: string): React.CSSProperties {
    if (!userClub || !teamName) return teamNameStyle;

    const searchTerms: string[] = [];
    if (userClub.name) searchTerms.push(userClub.name.toLowerCase());
    if (userClub.shortName) searchTerms.push(userClub.shortName.toLowerCase());
    const nameWords = userClub.name.split(' ').filter(w => w.length > 3);
    nameWords.forEach(word => searchTerms.push(word.toLowerCase()));

    const teamLower = teamName.toLowerCase();
    if (searchTerms.some(term => teamLower.includes(term))) {
      return { ...teamNameStyle, fontWeight: 'bold', color: '#4CAF50' };
    }
    return teamNameStyle;
  }
};

// Styles
const containerStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  padding: '24px',
  margin: '20px 0',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  flexWrap: 'wrap',
  gap: '12px',
};

const titleStyle: React.CSSProperties = { margin: 0, fontSize: '1.5rem' };
const clubInfoStyle: React.CSSProperties = { display: 'flex', gap: '8px', alignItems: 'center' };
const clubBadgeStyle: React.CSSProperties = { backgroundColor: '#e3f2fd', padding: '4px 12px', borderRadius: '16px', fontSize: '0.9rem' };
const leagueBadgeStyle: React.CSSProperties = { backgroundColor: '#f5f5f5', padding: '4px 12px', borderRadius: '16px', fontSize: '0.85rem' };

const errorStyle: React.CSSProperties = { backgroundColor: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '8px', marginBottom: '16px' };

const tabContainerStyle: React.CSSProperties = { display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #e0e0e0', paddingBottom: '12px' };
const tabStyle: React.CSSProperties = { padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.95rem', borderRadius: '8px 8px 0 0' };
const activeTabStyle: React.CSSProperties = { ...tabStyle, backgroundColor: '#1976d2', color: '#fff' };

const configContainerStyle: React.CSSProperties = { };
const formRowStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' };
const formGroupStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '6px' };
const labelStyle: React.CSSProperties = { fontWeight: 500, fontSize: '0.9rem', color: '#555' };
const selectStyle: React.CSSProperties = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '1rem' };
const inputStyle: React.CSSProperties = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '1rem' };

const checkboxContainerStyle: React.CSSProperties = { display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '24px' };
const checkboxLabelStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };

const buttonContainerStyle: React.CSSProperties = { display: 'flex', gap: '12px', justifyContent: 'flex-end' };
const primaryButtonStyle: React.CSSProperties = { padding: '12px 24px', backgroundColor: '#1976d2', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem' };
const secondaryButtonStyle: React.CSSProperties = { ...primaryButtonStyle, backgroundColor: '#757575' };
const importButtonStyle: React.CSSProperties = { ...primaryButtonStyle, backgroundColor: '#4CAF50' };

const previewContainerStyle: React.CSSProperties = { };
const previewHeaderStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' };
const filterBadgeStyle: React.CSSProperties = { backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '4px 12px', borderRadius: '16px', fontSize: '0.85rem' };

const matchListStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' };
const matchCardStyle: React.CSSProperties = { backgroundColor: '#f9f9f9', padding: '12px 16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' };
const matchDateStyle: React.CSSProperties = { fontSize: '0.85rem', color: '#666', minWidth: '160px' };
const matchTeamsStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px', flex: 1 };
const teamNameStyle: React.CSSProperties = { fontSize: '0.95rem' };
const vsStyle: React.CSSProperties = { color: '#999', fontSize: '0.8rem' };
const matchInfoStyle: React.CSSProperties = { fontSize: '0.8rem', color: '#888' };

const resultContainerStyle: React.CSSProperties = { };
const successBoxStyle: React.CSSProperties = { backgroundColor: '#e8f5e9', padding: '20px', borderRadius: '12px', marginBottom: '20px', textAlign: 'center' as const };
const errorBoxStyle: React.CSSProperties = { ...successBoxStyle, backgroundColor: '#ffebee' };

const statsGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '20px' };
const statCardStyle: React.CSSProperties = { backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '12px', textAlign: 'center' as const };
const statNumberStyle: React.CSSProperties = { fontSize: '2rem', fontWeight: 'bold', color: '#1976d2' };
const statLabelStyle: React.CSSProperties = { fontSize: '0.85rem', color: '#666', marginTop: '4px' };

const warningsStyle: React.CSSProperties = { backgroundColor: '#fff3e0', padding: '16px', borderRadius: '8px', marginBottom: '16px' };
const errorsStyle: React.CSSProperties = { backgroundColor: '#ffebee', padding: '16px', borderRadius: '8px', marginBottom: '16px' };

export default OpenLigaDbImport;

